/**
 * IELTS Reading, General Training (D-105): 3 sections, free navigation for the 60 minutes, as on
 * the test computer. On a computer the texts sit on the left and the questions on the right; on
 * a phone « Text » and « Questions » are two tabs. A grid of the 40 numbers moves anywhere; each
 * answer is saved at once and can be changed until the end.
 */

import { academyAttemptsNextCreate } from "@agencyos/api-client";
import { type IeltsState, instructionFor } from "@agencyos/core";
import { Button, SegmentedControl } from "@agencyos/ui-web";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { SectionDone } from "../McqSection";
import { ExamBar, ExamNav, ExamPill, FinishDialog, revealItem } from "../ExamFrame";
import { ExamPictureView } from "../ExamPicture";
import { TableCharts } from "../TableCharts";
import { QuestionBlock, type SittingQuestion, numberLabel, useAnswers } from "./IeltsQuestions";
import { useContentLang } from "./contentLang";

type Pane = "text" | "questions";

export function ReadingSection({ attemptId, state, onChanged }: { attemptId: string; state: IeltsState; onChanged: () => Promise<void> }) {
  const { t } = useTranslation();
  const contentLang = useContentLang(); // French for the TEF (D-109)
  const sections = state.sections;
  const [current, setCurrent] = useState(0);
  const [pane, setPane] = useState<Pane>("text");
  const [focused, setFocused] = useState<number | null>(null); // the number the learner is on
  const [confirm, setConfirm] = useState(false);
  const [closing, setClosing] = useState(false);
  const { set, flush, valueOf, isAnswered, saves } = useAnswers(attemptId, onChanged);

  // Every question with its number and its section (for the grid).
  const numbered = useMemo(() => {
    let running = 0;
    return sections.map((section, s) =>
      section.questions.map((question) => {
        const { first } = numberLabel(question, running + 1);
        running = first + Math.max(question.marks, 1) - 1;
        return { question, first, section: s };
      }),
    );
  }, [sections]);

  const all: SittingQuestion[] = sections.flatMap((s) => s.questions);
  const finish = async () => {
    setClosing(true);
    try {
      await flush(all);
      await academyAttemptsNextCreate(attemptId);
    } catch {
      // Already closed (time up in another tab): the refreshed state shows the next section.
    } finally {
      await onChanged();
      setClosing(false);
    }
  };

  const section = sections[current];
  if (!section) return <SectionDone onContinue={() => void finish()} busy={closing} />;
  const unanswered = all.length - all.filter(isAnswered).length;
  const sectionName = (index: number) =>
    state.exam === "tef_canada" ? t("tef.player.documentN", { n: index + 1 }) : t("ielts.player.sectionN", { n: index + 1 });
  const goTo = (s: number, first?: number) => {
    setCurrent(s);
    setFocused(first ?? null);
    setPane("questions");
    if (first !== undefined) revealItem(`iq-anchor-${first}`);
  };

  return (
    <div className="ac-reading">
      <div className="ac-reading__tabs">
        <div className="ac-task-tabs" role="tablist" aria-label={t("ielts.player.sections")}>
          {sections.map((item, index) => {
            const done = item.questions.filter(isAnswered).length;
            return (
              <button
                key={item.passage?.id ?? index}
                type="button"
                role="tab"
                aria-selected={index === current}
                className={`ac-task-tab${done === item.questions.length ? " ac-task-tab--sent" : ""}`}
                onClick={() => goTo(index)}
              >
                <span className="ac-task-tab__n ao-num" aria-hidden="true">
                  {index + 1}
                </span>
                <span>{sectionName(index)}</span>
                <span className="ac-task-tab__words ao-num" aria-hidden="true">
                  {done}/{item.questions.length}
                </span>
              </button>
            );
          })}
        </div>
        <div className="ac-reading__pane-switch">
          <SegmentedControl<Pane>
            label={t("ielts.player.pane")}
            value={pane}
            block
            options={[
              { value: "text", label: t("ielts.player.paneText") },
              { value: "questions", label: t("ielts.player.paneQuestions") },
            ]}
            onChange={(value) => setPane(value)}
          />
        </div>
      </div>

      <div className={`ac-reading__split ac-reading__split--${pane}`}>
        <section className="ac-question__sheet ac-reading__texts" aria-label={t("ielts.player.paneText")}>
          {section.passage?.instructions && (
            <p className="ac-item__instructions" lang={contentLang}>
              {section.passage.instructions}
            </p>
          )}
          <ExamPictureView picture={section.passage?.image ?? null} lang={contentLang} />
          {(section.passage?.texts ?? []).map((text, index) => (
            <article key={index} className="ac-reading__text" lang={contentLang}>
              {text.title && <h2 className="ac-title-3">{text.title}</h2>}
              {text.text.split(/\n{2,}|\n(?=[A-H] [–-] )/).map((paragraph, i) => (
                <p key={i}>{paragraph}</p>
              ))}
            </article>
          ))}
          {/* D-163: the graphs of TEF CE section 3 (question 22) */}
          <TableCharts table={section.passage?.table ?? null} lang={contentLang} />
        </section>
        <section className="ac-question__sheet ac-reading__questions" aria-label={t("ielts.player.paneQuestions")}>
          <ol
            className="ac-iq-list"
            onFocus={(event) => {
              const anchor = (event.target as HTMLElement).closest?.("[id^='iq-anchor-']");
              if (anchor) setFocused(Number(anchor.id.replace("iq-anchor-", "")));
            }}
          >
            {(numbered[current] ?? []).map(({ question, first }, index) => (
              <QuestionBlock
                key={question.id}
                rubric={instructionFor(section.passage?.instructionGroups ?? [], question.number, question.numbers, index === 0)}
                skill="ce"
                exam={state.exam || "ielts_general"}
                docType={section.passage?.docType ?? ""}
                question={question}
                fallbackNumber={first}
                value={valueOf(question)}
                onChange={(value) => set(question, value)}
                saveState={saves[question.id]}
              />
            ))}
          </ol>
        </section>
      </div>

      <ExamBar
        left={
          <Button variant="secondary" disabled={current === 0} onClick={() => goTo(current - 1)}>
            {t("ielts.player.prevSection")}
          </Button>
        }
        pills={<ExamPill>{sectionName(current)}</ExamPill>}
        right={
          current < sections.length - 1 ? (
            <Button onClick={() => goTo(current + 1)}>{t("ielts.player.nextSection")}</Button>
          ) : (
            <Button onClick={() => setConfirm(true)}>{t("academy.navigator.finish")}</Button>
          )
        }
      />

      <ExamNav
        cells={numbered.flat().map(({ question, first, section: s }) => ({
          key: question.id,
          text: numberLabel(question, first).text,
          state: isAnswered(question) ? "done" : "empty",
          current: first === (focused ?? numbered[current]?.[0]?.first) && s === current,
          onSelect: () => goTo(s, first),
        }))}
      >
        <Button variant="secondary" block onClick={() => setConfirm(true)}>
          {t("academy.navigator.finish")}
        </Button>
      </ExamNav>
      <FinishDialog open={confirm} unanswered={unanswered} busy={closing} onCancel={() => setConfirm(false)} onConfirm={() => void finish()} />
    </div>
  );
}
